import { ESocketEvents } from '@lbs/shared-rp-types'
import { devEventQueue } from '../../utils/devEventQueue'
import {
  ERpEvent,
  ERpService,
  JOIN_GAME_TIMEOUT_ERROR,
  RussianPokerJoinSuccessPayload,
  RussianPokerMachineContext,
  RussianPokerMachineRuntime,
} from './types'

const JOIN_GAME_TIMEOUT_MS = 7000

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  try {
    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(JOIN_GAME_TIMEOUT_ERROR))
      }, timeoutMs)
    })

    return await Promise.race([promise, timeoutPromise])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

export const createRpServices = (
  getRuntime: () => RussianPokerMachineRuntime
) =>
  ({
    [ERpService.JoinGameRequest]:
      (ctx: RussianPokerMachineContext) => (sendBack: (event: any) => void) => {
        let isCancelled = false

        ;(async () => {
          try {
            const { socket, accessToken } = getRuntime()

            const joinResult = (await withTimeout(
              socket.emitWithAck(ESocketEvents.JOIN_GAME, {
                gameId: ctx.gameId,
                accessToken,
              }),
              JOIN_GAME_TIMEOUT_MS
            )) as RussianPokerJoinSuccessPayload

            if (isCancelled) return

            if (joinResult?.success) {
              sendBack({ type: ERpEvent.JoinSuccess, data: joinResult })
              return
            }

            sendBack({
              type: ERpEvent.JoinFail,
              error: new Error('Join game response: success=false'),
            })
          } catch (error) {
            if (isCancelled) return
            sendBack({ type: ERpEvent.JoinFail, error })
          }
        })()

        return () => {
          isCancelled = true
        }
      },

    [ERpService.ListenGameEvents]:
      (ctx: RussianPokerMachineContext) => (sendBack: (event: any) => void) => {
        const { socket } = getRuntime()
        let currentSessionId = ctx.sessionId

        const { enqueue } = devEventQueue

        // isDealing reads machine state directly — avoids MobX/XState phase mismatch.
        // Used to route SESSION dealer-card updates into the animation queue.
        const isDealing = () => getRuntime().isInDealingPhase()

        // Pure adapter: never mutates the store directly.
        // All store updates happen in machine actions for atomic MobX/XState transitions.
        const handleGameEvent = (data: any) => {
          enqueue('GAME', data, () => {
            if (
              data?.sessionId != null &&
              data.sessionId !== currentSessionId
            ) {
              currentSessionId = data.sessionId
              sendBack({
                type: ERpEvent.NewSession,
                sessionId: data.sessionId,
                gamePatch: data,
              })
              return
            }
            sendBack({ type: ERpEvent.GamePatchReceived, gamePatch: data })
          })
        }

        const handleSessionEvent = (data: any) => {
          enqueue('SESSION', data, () => {
            const phase = data?.data?.phase
            const hasCards = data?.data?.cards !== undefined
            const boxOnTurn = data?.data?.boxOnTurn

            // During dealing, dealer card updates are routed through the animation queue.
            // The machine action (ApplyNextCard) applies the patch when the animation fires.
            if (isDealing() && hasCards && !phase) {
              sendBack({
                type: ERpEvent.CardQueued,
                item: { target: 'dealer', data },
              })
              return
            }

            // Events with phase: carry raw data so the machine action
            // (ApplySessionPatch) applies the patch atomically with the state transition.
            if (phase) {
              sendBack({
                type: ERpEvent.SessionPhaseReceived,
                phase,
                sessionPatch: data,
              })
            }

            if (boxOnTurn !== undefined) {
              sendBack({
                type: ERpEvent.BoxTurnReceived,
                seatNumber: boxOnTurn,
                sessionPatch: data,
              })
            }

            // No phase and no boxOnTurn: generic session update, applied via machine action.
            if (!phase && boxOnTurn === undefined) {
              sendBack({
                type: ERpEvent.SessionPatchReceived,
                sessionPatch: data,
              })
            }
          })
        }

        // Pure adapter: normalizes every BOX event to BoxPatchReceived.
        // Guards: seatNumber must be a number — skip malformed events.
        // The machine decides routing:
        //   dealing    → EnqueueBoxPatch (animation) + StoreDeferredBoxPatch (buffer)
        //   decisions  → apply or buffer based on boxOnTurn match
        //   other      → ApplyBoxPatch (playing.on catch-all)
        const handleBoxEvent = (data: any) => {
          enqueue('BOX', data, () => {
            const seatNumber = data?.target?.seatNumber
            if (typeof seatNumber !== 'number') return
            sendBack({
              type: ERpEvent.BoxPatchReceived,
              seatNumber,
              boxPatch: data,
            })
          })
        }

        const handleDepositEvent = (data: any) => {
          enqueue('DEPOSIT', data, () => {
            sendBack({ type: ERpEvent.DepositReceived, depositData: data })
          })
        }

        const handleLeaveGameEvent = () => {
          enqueue('LEAVE_GAME', null, () =>
            sendBack({ type: ERpEvent.ServerLeaveGame })
          )
        }

        socket.on(ESocketEvents.GAME, handleGameEvent)
        socket.on(ESocketEvents.SESSION, handleSessionEvent)
        socket.on(ESocketEvents.BOX, handleBoxEvent)
        socket.on(ESocketEvents.DEPOSIT, handleDepositEvent)
        socket.on(ESocketEvents.LEAVE_GAME, handleLeaveGameEvent)

        return () => {
          socket.off(ESocketEvents.GAME, handleGameEvent)
          socket.off(ESocketEvents.SESSION, handleSessionEvent)
          socket.off(ESocketEvents.BOX, handleBoxEvent)
          socket.off(ESocketEvents.DEPOSIT, handleDepositEvent)
          socket.off(ESocketEvents.LEAVE_GAME, handleLeaveGameEvent)
        }
      },
  } as const)
